import { ModalV2 } from '@components'
import { NocfoPlan } from '@constants'
import { BillingInterval } from '@query'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { BillingSetupPanel } from './BillingSetupPanel'

type BillingSetupMode = 'plan-order' | 'einvoicing' | 'payment-method'

interface Props {
  isVisible: boolean
  onClose: () => void
  mode: BillingSetupMode
  selectedPlan?: NocfoPlan
  selectedBillingInterval?: BillingInterval
  /** Pre-select a billing option when the modal opens (e.g. manual invoicing). */
  initialSelectedCardId?: string | null
  onStripeAction: () => void
  isStripeActionPending?: boolean
  onManualSubmitSuccess?: () => void
}

export const BillingSetupModal: React.FC<Props> = ({
  isVisible,
  onClose,
  mode,
  selectedPlan,
  selectedBillingInterval,
  initialSelectedCardId = null,
  onStripeAction,
  isStripeActionPending = false,
  onManualSubmitSuccess
}) => {
  const [t] = useTranslation()

  const title =
    mode === 'plan-order'
      ? t('components.billingSetup.modal.planOrderTitle')
      : mode === 'payment-method'
      ? t('components.billingSetup.modal.paymentMethodTitle')
      : t('settings.einvoicing.paymentSetupRequiredTitle')

  const description =
    mode === 'plan-order'
      ? t('components.billingSetup.modal.planOrderDescription')
      : mode === 'payment-method'
      ? t('components.billingSetup.modal.paymentMethodDescription')
      : t('settings.einvoicing.paymentSetupRequiredDescription')

  const handleManualSuccess = () => {
    onManualSubmitSuccess?.()
    onClose()
  }

  return (
    <ModalV2 isVisible={isVisible} handleOnClose={onClose} width={720} height={860} header={title}>
      <ModalBody>
        <Intro>{description}</Intro>
        <BillingSetupPanel
          mode={mode}
          selectedPlan={selectedPlan}
          selectedBillingInterval={selectedBillingInterval}
          initialSelectedCardId={initialSelectedCardId}
          onStripeAction={onStripeAction}
          isStripeActionPending={isStripeActionPending}
          onManualSubmitSuccess={handleManualSuccess}
        />
      </ModalBody>
    </ModalV2>
  )
}

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg}rem;
  padding: ${({ theme }) => theme.spacing.xs}rem 0;
`

const Intro = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  line-height: 1.5;
`
